/**
 * App — Lodestar route shell.
 *
 * Phase W (perf): all routes lazy-loaded via React.lazy + Suspense.
 *   • Landing is the only synchronous page (first-paint critical path).
 *   • Every other route loads as a webpack chunk only when navigated to.
 *   • CRITICAL: Emergent OAuth hash-redirect detection happens BEFORE any
 *     lazy route mounts to preserve session-id integrity.
 */
import "@/App.css";
import "@/i18n";  // bootstraps i18next + localStorage persistence before any component mounts
import { Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { lazyWithRetry as lazy } from "@/lib/lazyWithRetry";  // auto-recovers stale-chunk "Unexpected token '<'" after deploys

import { AuthProvider } from "@/contexts/AuthContext";
import RequireAuth from "@/components/RequireAuth";
import Header from "@/components/Header";
import Disclaimer from "@/components/Disclaimer";
import RouteFallback from "@/components/RouteFallback";
import CookieConsentBanner from "@/components/CookieConsentBanner";
import RouteAnalytics from "@/components/RouteAnalytics";
// Toaster renders nothing until a `toast()` call fires — push its Radix
// dependency off the critical-path bundle by lazy-importing it. Suspended
// with `null` fallback so it stays invisible until the user triggers
// something that actually wants a toast.
const Toaster = lazy(() =>
    import("@/components/ui/toaster").then((m) => ({ default: m.Toaster }))
);

// Landing is the public homepage — load eagerly for instant first paint.
import Landing from "@/pages/Landing";

// Phase 1 — conversational "Ask the Universe Anything" landing.
// Gated behind REACT_APP_CHAT_LANDING=1; falls back to classic Landing
// at `/` when the flag is off. When on, the classic page is reachable
// at `/classic` as an escape hatch.
const CHAT_LANDING_ENABLED = process.env.REACT_APP_CHAT_LANDING === "1";
const ChatLanding = lazy(() => import("@/pages/ChatLanding"));

// Every other route loads on-demand via webpack code-splitting.
const BirthForm          = lazy(() => import("@/pages/BirthForm"));
const Dashboard          = lazy(() => import("@/pages/Dashboard"));
const Login              = lazy(() => import("@/pages/Login"));
const Register           = lazy(() => import("@/pages/Register"));
const Account            = lazy(() => import("@/pages/Account"));
const AuthCallback       = lazy(() => import("@/pages/AuthCallback"));
const ForgotPassword     = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword      = lazy(() => import("@/pages/ResetPassword"));
const Pricing            = lazy(() => import("@/pages/Pricing"));
const Admin              = lazy(() => import("@/pages/Admin"));
const DeepDive           = lazy(() => import("@/pages/DeepDive"));
const Horary             = lazy(() => import("@/pages/Horary"));
const HoraryShareView    = lazy(() => import("@/pages/HoraryShareView"));
const Synastry           = lazy(() => import("@/pages/Synastry"));
const SynastryShareView  = lazy(() => import("@/pages/SynastryShareView"));
const CareerPicker       = lazy(() => import("@/pages/CareerPicker"));
const WealthPicker       = lazy(() => import("@/pages/WealthPicker"));
const ReportsPicker      = lazy(() => import("@/pages/ReportsPicker"));
const ChatPicker         = lazy(() => import("@/pages/ChatPicker"));
const Remedies           = lazy(() => import("@/pages/Remedies"));
const Today              = lazy(() => import("@/pages/Today"));
const Horoscope          = lazy(() => import("@/pages/Horoscope"));
const Compatibility      = lazy(() => import("@/pages/Compatibility"));
const DashaHub           = lazy(() => import("@/pages/DashaHub"));
const DashaDetail        = lazy(() => import("@/pages/DashaDetail"));
const LearnHub           = lazy(() => import("@/pages/LearnHub"));
const LearnArticle       = lazy(() => import("@/pages/LearnArticle"));
const Privacy            = lazy(() => import("@/pages/Privacy"));
const Terms              = lazy(() => import("@/pages/Terms"));
const Contact            = lazy(() => import("@/pages/Contact"));
const Consult            = lazy(() => import("@/pages/Consult"));
const LegalDisclaimer    = lazy(() => import("@/pages/LegalDisclaimer"));
const Refund             = lazy(() => import("@/pages/Refund"));
const Methodology        = lazy(() => import("@/pages/Methodology"));
const Glossary           = lazy(() => import("@/pages/Glossary"));
const Sample             = lazy(() => import("@/pages/Sample"));
const MagicLogin         = lazy(() => import("@/pages/MagicLogin"));
const LanguagePrefixRoute = lazy(() => import("@/components/LanguagePrefixRoute"));

function Shell() {
    const location = useLocation();
    // CRITICAL: synchronously detect session_id in URL hash (returning from Emergent OAuth).
    // Must run BEFORE any auth check / redirect to avoid race.
    if (location.hash?.includes("session_id=")) {
        return (
            <>
                <Header />
                <main className="flex-1 pb-12">
                    <Suspense fallback={<RouteFallback />}>
                        <AuthCallback />
                    </Suspense>
                </main>
                <Disclaimer />
            </>
        );
    }
    return (
        <>
            <Header />
            <RouteAnalytics />
            <main className="flex-1 pb-12">
                <Suspense fallback={<RouteFallback />}>
                    <Routes>
                        {CHAT_LANDING_ENABLED ? (
                            <>
                                <Route path="/" element={<ChatLanding />} />
                                <Route path="/classic" element={<Landing />} />
                            </>
                        ) : (
                            <Route path="/" element={<Landing />} />
                        )}
                        <Route path="/start" element={<BirthForm />} />
                        <Route path="/roadmap/:hash" element={<Dashboard />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password/:token" element={<ResetPassword />} />
                        <Route path="/pricing" element={<Pricing />} />
                        <Route path="/horary" element={<Horary />} />
                        {/* /ask → /horary — marketing-link continuity. Uses `replace`
                            so the browser back button doesn't get stuck bouncing
                            between /ask and /horary, and any external links that
                            still point at /ask keep working. */}
                        <Route path="/ask" element={<Navigate to="/horary" replace />} />
                        <Route path="/horary/share/:id" element={<HoraryShareView />} />
                        <Route path="/synastry" element={<Synastry />} />
                        <Route path="/synastry/share/:id" element={<SynastryShareView />} />
                        {/* Sidebar deep-links — each gated by RequireAuth so anon
                            visitors get prompted to sign-in. Direct URL access is
                            supported because Routes are top-level. */}
                        <Route
                            path="/profile"
                            element={<Navigate to="/account#profile" replace />}
                        />
                        <Route
                            path="/career"
                            element={<RequireAuth><CareerPicker /></RequireAuth>}
                        />
                        <Route
                            path="/wealth"
                            element={<RequireAuth><WealthPicker /></RequireAuth>}
                        />
                        <Route
                            path="/reports"
                            element={<RequireAuth><ReportsPicker /></RequireAuth>}
                        />
                        <Route
                            path="/chat"
                            element={<RequireAuth><ChatPicker /></RequireAuth>}
                        />
                        <Route path="/remedies" element={<Remedies />} />
                        {/* Public, indexable "today's cosmic weather" page.
                            SEO bait + IG-story share format. No auth. */}
                        <Route path="/today" element={<Today />} />
                        {/* Programmatic SEO long-tail pages.
                            12 horoscope/{sign} + 66 compatibility/{a}-{b} =
                            78 pages indexed off one set of components.
                            See backend/seo_pages.py for content. */}
                        <Route path="/horoscope/:sign" element={<Horoscope />} />
                        <Route path="/compatibility/:pair" element={<Compatibility />} />
                        {/* /dasha hub + /dasha/{md-bh} detail — 81 programmatic
                            Vimshottari mahadasha × bhukti pages (iter 56). */}
                        <Route path="/dasha" element={<DashaHub />} />
                        <Route path="/dasha/:pair" element={<DashaDetail />} />
                        {/* Learning Center — 12 evergreen articles + hub. */}
                        <Route path="/learn" element={<LearnHub />} />
                        <Route path="/learn/:slug" element={<LearnArticle />} />
                        {/* Tier-1 legal & trust surfaces (iter 54) */}
                        <Route path="/privacy" element={<Privacy />} />
                        <Route path="/terms" element={<Terms />} />
                        <Route path="/contact" element={<Contact />} />
                        <Route path="/consult" element={<Consult />} />
                        <Route path="/disclaimer" element={<LegalDisclaimer />} />
                        <Route path="/refund" element={<Refund />} />
                        <Route path="/methodology" element={<Methodology />} />
                        <Route path="/glossary" element={<Glossary />} />
                        <Route path="/sample" element={<Sample />} />
                        {/* Weekly Cosmic Forecast magic-link redemption.
                            POSTs the token to the backend, sets the session
                            cookie, then redirects to /today (or the path
                            specified by `?next=`). */}
                        <Route path="/magic/:token" element={<MagicLogin />} />
                        <Route
                            path="/account"
                            element={
                                <RequireAuth>
                                    <Account />
                                </RequireAuth>
                            }
                        />
                        <Route
                            path="/admin"
                            element={
                                <RequireAuth>
                                    <Admin />
                                </RequireAuth>
                            }
                        />
                        <Route
                            path="/deep-dive/:domain/:hash"
                            element={
                                <RequireAuth>
                                    <DeepDive />
                                </RequireAuth>
                            }
                        />

                        {/* SEO locale-prefixed routes — `/hi/start`,
                            `/ta/horary`, `/es/synastry`, etc. The prefix
                            shim sets i18n.language on mount and renders the
                            same child page via <Outlet />. English remains
                            unprefixed (the canonical site). Unknown prefixes
                            redirect to the un-prefixed equivalent so bots
                            can't crawl `/klingon/foo` junk. */}
                        <Route path="/:lang" element={<LanguagePrefixRoute />}>
                            <Route index element={<Landing />} />
                            <Route path="start" element={<BirthForm />} />
                            <Route path="horary" element={<Horary />} />
                            <Route path="pricing" element={<Pricing />} />
                            <Route path="synastry" element={<Synastry />} />
                            <Route path="remedies" element={<Remedies />} />
                            <Route path="today" element={<Today />} />
                        </Route>
                    </Routes>
                </Suspense>
            </main>
            <Disclaimer />
        </>
    );
}

export default function App() {
    return (
        <div className="App min-h-screen flex flex-col">
            <HelmetProvider>
                <BrowserRouter>
                    <AuthProvider>
                        <Shell />
                    </AuthProvider>
                </BrowserRouter>
                <Suspense fallback={null}><Toaster /></Suspense>
                <CookieConsentBanner />
            </HelmetProvider>
        </div>
    );
}
